// src/pages/Ligue/ResultsCalendrierLigue.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaSyncAlt,
  FaSortAmountDown,
  FaSortAmountUp,
  FaExternalLinkAlt,
  FaImage,
  FaListAlt,
  FaFileAlt,
} from "react-icons/fa";
import { getAllResultsEvents } from "../../../services/resultsEventServices";
import HeroImage from "../../../components/Hero/HeroImage";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

/* ===================== Catégories d’âge ===================== */
const AGE_CATEGORIES = [
  "Eveil",
  "Mini-Poussin(e)",
  "Poussin(e)",
  "Benjamin(e)",
  "Minime",
  "Cadet(te)",
  "Junior",
  "Sénior",
  "Vétéran",
  "ADULT",
  "U-21",
  "U-18",
  "U-16",
  "U-14",
  "U-12",
  "U-10",
  "U-8",
];

/* ===================== Helpers ===================== */
const normalize = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

function toCanonicalCategory(token) {
  const t = normalize(token);
  if (t.includes("eveil")) return "Eveil";
  if (t.includes("mini") && t.includes("poussin")) return "Mini-Poussin(e)";
  if (!t.includes("mini") && t.includes("poussin")) return "Poussin(e)";
  if (t.includes("benjamin")) return "Benjamin(e)";
  if (t.includes("minime")) return "Minime";
  if (t.includes("cadet")) return "Cadet(te)";
  if (t.includes("junior")) return "Junior";
  if (t.includes("senior") || t.includes("sénior")) return "Sénior";
  if (t.includes("veteran")) return "Vétéran";
  if (t.includes("adult")) return "ADULT";
  if (t.includes("u-21")) return "U-21";
  if (t.includes("u-18")) return "U-18";
  if (t.includes("u-16")) return "U-16";
  if (t.includes("u-14")) return "U-14";
  if (t.includes("u-12")) return "U-12";
  if (t.includes("u-10")) return "U-10";
  if (t.includes("u-8")) return "U-8";
  return null;
}
function extractEventCategories(cate_event) {
  const parts = String(cate_event || "")
    .split(/[,/|;]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
  const set = new Set();
  for (const p of parts) {
    const canon = toCanonicalCategory(p);
    if (canon) set.add(canon);
  }
  return AGE_CATEGORIES.filter((c) => set.has(c));
}
function toDateSafe(value) {
  const d = value ? new Date(value) : null;
  return isNaN(d?.getTime?.()) ? null : d;
}
function formatDateFR(value) {
  const d = toDateSafe(value);
  if (!d) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`; // DD/MM/YY
}
function getYear(value) {
  const d = toDateSafe(value);
  return d ? d.getFullYear() : null;
}

export default function ResultsCalendrierLigue() {
  useScrollToTop();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtres & UI
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try {
      return (
        window.matchMedia && window.matchMedia("(min-width:1024px)").matches
      );
    } catch {
      return true;
    }
  });
  const [search, setSearch] = useState("");
  const [selectedAges, setSelectedAges] = useState([]);
  const [selectedOrganisateurs, setSelectedOrganisateurs] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc"); // desc = récents d'abord

  /* ===================== Load ===================== */
  const fetchResults = async () => {
    setLoading(true);
    try {
      const data = await getAllResultsEvents(); // peut accepter { q, limit, offset } si besoin
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchResults();
  }, []);

  /* ===================== Valeurs filtres ===================== */
  const allOrganisateurs = useMemo(
    () =>
      Array.from(new Set(rows.map((e) => e.organisateur).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, "fr")
      ),
    [rows]
  );
  const allTypes = useMemo(
    () =>
      Array.from(new Set(rows.map((e) => e.type_event).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, "fr")
      ),
    [rows]
  );
  const allLevels = useMemo(
    () =>
      Array.from(new Set(rows.map((e) => e.level_event).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b, "fr")
      ),
    [rows]
  );
  const allYears = useMemo(() => {
    const set = new Set(rows.map((e) => getYear(e.start)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [rows]);

  /* ===================== Helpers filtres ===================== */
  const toggleValue = (value, arr, setter) => {
    setter(
      arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
    );
  };
  const clearAllFilters = () => {
    setSearch("");
    setSelectedAges([]);
    setSelectedOrganisateurs([]);
    setSelectedTypes([]);
    setSelectedLevels([]);
    setSelectedYears([]);
    setSortOrder("desc");
  };

  /* ===================== Filtrage + tri ===================== */
  const filtered = useMemo(() => {
    let arr = rows.slice();

    // Recherche plein texte
    if (search.trim()) {
      const q = normalize(search);
      arr = arr.filter((e) => {
        const fields = [
          e.titre,
          e.lieu_event,
          e.organisateur,
          e.type_event,
          e.level_event,
          e.cate_event,
        ]
          .map((x) => normalize(x || ""))
          .join(" ");
        return fields.includes(q);
      });
    }

    // Organisateurs / Types / Niveaux
    if (selectedOrganisateurs.length > 0) {
      const set = new Set(selectedOrganisateurs);
      arr = arr.filter((e) => set.has(e.organisateur));
    }
    if (selectedTypes.length > 0) {
      const set = new Set(selectedTypes);
      arr = arr.filter((e) => set.has(e.type_event));
    }
    if (selectedLevels.length > 0) {
      const set = new Set(selectedLevels);
      arr = arr.filter((e) => set.has(e.level_event));
    }

    // Catégories d’âge
    if (selectedAges.length > 0) {
      const selectedSet = new Set(selectedAges);
      arr = arr.filter((e) => {
        const cats = extractEventCategories(e.cate_event);
        if (cats.length === 0) return false;
        return cats.some((c) => selectedSet.has(c));
      });
    }

    // Années (sur start)
    if (selectedYears.length > 0) {
      const set = new Set(selectedYears);
      arr = arr.filter((e) => set.has(getYear(e.start)));
    }

    // Tri par date
    arr.sort((a, b) => {
      const da = toDateSafe(a.start)?.getTime?.() ?? 0;
      const db = toDateSafe(b.start)?.getTime?.() ?? 0;
      return sortOrder === "asc" ? da - db : db - da;
    });

    return arr;
  }, [
    rows,
    search,
    selectedAges,
    selectedOrganisateurs,
    selectedTypes,
    selectedLevels,
    selectedYears,
    sortOrder,
  ]);

  return (
    <div className="w-full pt-[70px]">
      <ScrollIndicator />

      <HeroImage
        title="Résultats – Ligue Nouvelle-Aquitaine"
        subtitle="Rapports, tableaux, sélections & photos"
        imageUrl="/img/cover/cover_podium.jpg"
      />

      {/* BARRE D’ACTIONS */}
      <div className="max-w-[1200px] mx-auto px-4 pt-6 pb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-semibold">
            {filtered.length} résultat{filtered.length > 1 ? "s" : ""}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
            aria-controls="filters-panel"
            className="px-3 py-2 rounded-lg border bg-white hover:bg-slate-50"
          >
            {filtersOpen ? "Masquer les filtres" : "Afficher les filtres"}
          </button>

          <button
            onClick={() => {
              clearAllFilters();
              fetchResults();
            }}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-slate-50"
            title="Réinitialiser tous les filtres"
          >
            <FaSyncAlt /> Réinitialiser
          </button>

          <button
            onClick={() => setSortOrder((s) => (s === "asc" ? "desc" : "asc"))}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-white hover:bg-slate-50"
            title="Inverser le tri par date"
          >
            {sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />}{" "}
            Tri par date
          </button>
        </div>
      </div>

      {/* ZONE RECHERCHE + FILTRES */}
      {filtersOpen && (
        <section
          id="filters-panel"
          className="max-w-[1200px] mx-auto px-4 pb-2 text-sm"
        >
          <div className="grid grid-cols-1 gap-3">
            {/* Recherche */}
            <div className="flex items-center gap-2 px-2.5 py-2 rounded-lg border bg-white dark:bg-neutral-800">
              <FaSearch className="opacity-70 text-xs" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (titre, lieu, organisateur…)"
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>

            {/* Années */}
            <div className="bg-white dark:bg-neutral-900 border rounded-xl p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold">Années</div>
                <div className="text-[11px] flex gap-2">
                  <button
                    className="underline"
                    onClick={() => setSelectedYears(allYears)}
                  >
                    Tout
                  </button>
                  <button
                    className="underline"
                    onClick={() => setSelectedYears([])}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {allYears.map((y) => (
                  <label
                    key={y}
                    className={`px-2 py-1.5 rounded-lg border cursor-pointer ${
                      selectedYears.includes(y)
                        ? "bg-primary/10 border-primary"
                        : "bg-white dark:bg-neutral-800"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs mr-2"
                      checked={selectedYears.includes(y)}
                      onChange={() =>
                        toggleValue(y, selectedYears, setSelectedYears)
                      }
                    />
                    <span className="text-xs">{y}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Catégories d’âge */}
            <div className="bg-white dark:bg-neutral-900 border rounded-xl p-2.5">
              <div className="text-xs font-semibold mb-1.5">
                Catégories d’âge
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-1.5">
                {AGE_CATEGORIES.map((label) => (
                  <label
                    key={label}
                    className="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-neutral-800 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedAges.includes(label)}
                      onChange={() =>
                        toggleValue(label, selectedAges, setSelectedAges)
                      }
                    />
                    <span className="text-xs">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Organisateurs */}
            <div className="bg-white dark:bg-neutral-900 border rounded-xl p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold">Organisateurs</div>
                <div className="text-[11px] flex gap-2">
                  <button
                    className="underline"
                    onClick={() => setSelectedOrganisateurs(allOrganisateurs)}
                  >
                    Tout
                  </button>
                  <button
                    className="underline"
                    onClick={() => setSelectedOrganisateurs([])}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-44 overflow-auto pr-1">
                {allOrganisateurs.map((o) => (
                  <label
                    key={o}
                    className="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-neutral-800 cursor-pointer"
                    title={o}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedOrganisateurs.includes(o)}
                      onChange={() =>
                        toggleValue(
                          o,
                          selectedOrganisateurs,
                          setSelectedOrganisateurs
                        )
                      }
                    />
                    <span className="truncate text-xs">{o}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Types */}
            <div className="bg-white dark:bg-neutral-900 border rounded-xl p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold">Types</div>
                <div className="text-[11px] flex gap-2">
                  <button
                    className="underline"
                    onClick={() => setSelectedTypes(allTypes)}
                  >
                    Tout
                  </button>
                  <button
                    className="underline"
                    onClick={() => setSelectedTypes([])}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {allTypes.map((t) => (
                  <label
                    key={t}
                    className="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-neutral-800 cursor-pointer"
                    title={t}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedTypes.includes(t)}
                      onChange={() =>
                        toggleValue(t, selectedTypes, setSelectedTypes)
                      }
                    />
                    <span className="truncate text-xs">{t}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Niveaux */}
            <div className="bg-white dark:bg-neutral-900 border rounded-xl p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-semibold">Niveaux</div>
                <div className="text-[11px] flex gap-2">
                  <button
                    className="underline"
                    onClick={() => setSelectedLevels(allLevels)}
                  >
                    Tout
                  </button>
                  <button
                    className="underline"
                    onClick={() => setSelectedLevels([])}
                  >
                    Aucun
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {allLevels.map((l) => (
                  <label
                    key={l}
                    className="inline-flex items-center gap-2 px-2 py-1.5 rounded-lg border bg-white dark:bg-neutral-800 cursor-pointer"
                    title={l}
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedLevels.includes(l)}
                      onChange={() =>
                        toggleValue(l, selectedLevels, setSelectedLevels)
                      }
                    />
                    <span className="truncate text-xs">{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TABLEAU DES RÉSULTATS */}
      <section className="max-w-[1200px] mx-auto px-4 pb-10">
        {loading ? (
          <div className="p-8 text-center">Chargement des résultats…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center opacity-75">
            Aucun résultat ne correspond à vos filtres.
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-xl bg-white dark:bg-neutral-900">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="sticky top-0 bg-white dark:bg-neutral-900 z-10">
                <tr className="text-left border-b">
                  <th className="px-3 py-2 w-32">Date</th>
                  <th className="px-3 py-2 w-[420px]">Titre</th>
                  <th className="px-3 py-2 w-36">Type</th>
                  <th className="px-3 py-2 w-36">Niveau</th>
                  <th className="px-3 py-2">Catégories</th>
                  <th className="px-3 py-2 w-48">Lieu</th>
                  <th className="px-3 py-2 w-36">Org.</th>
                  <th className="px-3 py-2 w-[240px]">Liens</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const cats = extractEventCategories(r.cate_event);
                  const hasRapport = !!r.rapport_url;
                  const hasTableaux = !!r.tableaux_url;
                  const hasSelection = !!r.selection_url;
                  const hasPhotos = !!r.photospodium_url;

                  return (
                    <tr key={r.id} className="border-b hover:bg-slate-50/60">
                      <td className="px-3 py-2 align-top">
                        {formatDateFR(r.start)}
                      </td>
                      <td className="px-3 py-2 align-top w-[420px]">
                        <div className="font-semibold leading-5 whitespace-pre-wrap break-words">
                          {r.titre || "—"}
                        </div>
                        {r.lieu_event && (
                          <div className="text-xs opacity-70 mt-0.5">
                            #{normalize(r.lieu_event)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="inline-block px-2 py-1 rounded bg-slate-100 dark:bg-neutral-800">
                          {r.type_event || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="inline-block px-2 py-1 rounded bg-slate-100 dark:bg-neutral-800">
                          {r.level_event || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-wrap gap-1">
                          {cats.length ? (
                            cats.map((c) => (
                              <span
                                key={c}
                                className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-neutral-800"
                              >
                                {c}
                              </span>
                            ))
                          ) : (
                            <span className="opacity-60">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="whitespace-pre-wrap break-words">
                          {r.lieu_event || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top w-36">
                        <div className="whitespace-pre-wrap break-words">
                          {r.organisateur || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-wrap gap-2">
                          {/* Rapport */}
                          {hasRapport && (
                            <a
                              href={r.rapport_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-slate-50"
                              title="Rapport (PDF)"
                            >
                              <FaFileAlt /> Rapport{" "}
                              <FaExternalLinkAlt className="opacity-60" />
                            </a>
                          )}
                          {/* Tableaux */}
                          {hasTableaux && (
                            <a
                              href={r.tableaux_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-slate-50"
                              title="Tableaux (PDF)"
                            >
                              <FaListAlt /> Tableaux{" "}
                              <FaExternalLinkAlt className="opacity-60" />
                            </a>
                          )}
                          {/* Sélection */}
                          {hasSelection && (
                            <a
                              href={r.selection_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-slate-50"
                              title="Sélection (PDF)"
                            >
                              <FaFileAlt /> Sélection{" "}
                              <FaExternalLinkAlt className="opacity-60" />
                            </a>
                          )}
                          {/* Photos podium */}
                          {hasPhotos && (
                            <a
                              href={r.photospodium_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border hover:bg-slate-50"
                              title="Photos podium"
                            >
                              <FaImage /> Photos{" "}
                              <FaExternalLinkAlt className="opacity-60" />
                            </a>
                          )}
                          {/* Aucun lien */}
                          {!hasRapport &&
                            !hasTableaux &&
                            !hasSelection &&
                            !hasPhotos && <span className="opacity-60">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
